import React, { useState } from "react";
import {
  Box, Typography, Card, CardContent, Button, TextField, Chip,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Stack,
} from "@mui/material";
import {
  GroupsOutlined as GroupsIcon,
  FileUploadOutlined as FileUploadIcon,
  DownloadOutlined as DownloadIcon,
  CheckCircleOutline as CheckCircleIcon,
  BoltOutlined as BoltIcon,
  InsightsOutlined as InsightsIcon,
} from "@mui/icons-material";
import { SCENARIOS, CUSTOM_SCENARIO_OPTION } from "../data/scenarios";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

// Match AIVoiceCall design tokens
const T = {
  pageBg: "#f8fafc",
  border: "#e5e7eb",
  borderHover: "#cbd5e1",
  text: "#0f172a",
  textMuted: "#64748b",
  textFaint: "#94a3b8",
  primary: "#4f46e5",
  primaryDark: "#4338ca",
  primarySoft: "#eef2ff",
  success: "#059669",
  successSoft: "#ecfdf5",
  warning: "#d97706",
  warningSoft: "#fffbeb",
  danger: "#dc2626",
  dangerSoft: "#fef2f2",
  info: "#0284c7",
  infoSoft: "#f0f9ff",
};

function VoiceAvatar({ name, size = 26 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <Box sx={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: T.primarySoft, color: T.primary,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.42, fontWeight: 700, border: `1px solid ${T.border}`,
    }}>
      {initial}
    </Box>
  );
}

/**
 * Bulk call dispatch panel — lives inside AI Voice as a tab.
 * Reuses the voice clones & caller-ID numbers already loaded by the parent.
 * Dispatched calls land in aiVoiceCalls, so the Operations tab shows their
 * transcripts and captured data exactly like single calls.
 */
export default function BulkCallPanel({ token, clones = [], phoneNumbers = [], phoneNumbersLoading = false, onDispatched }) {
  const headers = { Authorization: `Bearer ${token}` };

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [validation, setValidation] = useState(null); // { validCount, errorCount, errors }

  const [fromNumberId, setFromNumberId] = useState("");
  const [voiceCloneId, setVoiceCloneId] = useState("");
  const [scenarioId, setScenarioId] = useState(CUSTOM_SCENARIO_OPTION.id);
  const [scenarioPrompt, setScenarioPrompt] = useState("");
  const [rateLimit, setRateLimit] = useState(5);

  const [validating, setValidating] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [banner, setBanner] = useState(null);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setFileName(f.name);
    setValidation(null);
    setBanner(null);
    autoValidate(f);
  };

  const autoValidate = async (f) => {
    setValidating(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API}/api/bulk/validate`, { method: "POST", headers, body: fd });
      const data = await res.json();
      if (res.ok) {
        setValidation(data);
      } else {
        setBanner({ type: "error", text: data.error || "Could not read CSV" });
      }
    } catch {
      setBanner({ type: "error", text: "Could not read CSV" });
    } finally {
      setValidating(false);
    }
  };

  const downloadTemplate = () => {
    const csv = `phoneNumber,userName,customData
+919876543210,John Doe,
+919876543211,Jane Smith,`;
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_calls_template.csv";
    a.click();
  };

  const handleScenarioSelect = (id) => {
    setScenarioId(id);
    if (id === CUSTOM_SCENARIO_OPTION.id) return;
    const s = SCENARIOS.find((x) => x.id === id);
    if (s) setScenarioPrompt(s.prompt);
  };

  const validCount = validation?.validCount ?? 0;
  const canDispatch = file && validCount > 0 && voiceCloneId && scenarioPrompt && !dispatching;

  const handleDispatch = async () => {
    if (!file) return setBanner({ type: "error", text: "Upload a CSV of recipients first" });
    if (!voiceCloneId) return setBanner({ type: "error", text: "Select a voice" });
    if (!scenarioPrompt) return setBanner({ type: "error", text: "Select or write a scenario prompt" });

    setDispatching(true);
    setBanner(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("voiceCloneId", voiceCloneId);
      fd.append("scenarioPrompt", scenarioPrompt);
      fd.append("fromNumberId", fromNumberId);
      fd.append("rateLimit", rateLimit);

      const res = await fetch(`${API}/api/bulk/import`, { method: "POST", headers, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Dispatch failed");

      // Reset form
      setFile(null);
      setFileName("");
      setValidation(null);
      setScenarioId(CUSTOM_SCENARIO_OPTION.id);
      setScenarioPrompt("");

      if (onDispatched) onDispatched(data.totalCount || validCount);
    } catch (err) {
      setBanner({ type: "error", text: err.message });
    } finally {
      setDispatching(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" }, gap: 3 }}>
      {/* Left — main form */}
      <Card sx={{ border: `1px solid ${T.border}`, boxShadow: "none", borderRadius: "12px" }}>
        <CardContent sx={{ p: "28px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "8px", background: T.primarySoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GroupsIcon sx={{ fontSize: 20, color: T.primary }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: T.text, letterSpacing: "-0.01em" }}>Bulk Call Campaign</Typography>
              <Typography sx={{ fontSize: "0.82rem", color: T.textMuted, mt: 0.25 }}>Upload a recipient list and dispatch a rate-limited outbound campaign</Typography>
            </Box>
          </Box>

          {banner && (
            <Alert severity={banner.type} sx={{ mb: 2.5, borderRadius: "8px" }} onClose={() => setBanner(null)}>
              {banner.text}
            </Alert>
          )}

          {/* Step 1 — recipients */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Recipients
            </Typography>
            <Button
              size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} onClick={downloadTemplate}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.74rem", color: T.primary, "&:hover": { background: T.primarySoft } }}
            >
              CSV template
            </Button>
          </Box>

          <Box
            onClick={() => document.getElementById("bulk-csv-input").click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
            sx={{
              border: `1.5px dashed ${dragOver ? T.primary : T.border}`,
              background: dragOver ? T.primarySoft : T.pageBg,
              borderRadius: "10px", p: 3, textAlign: "center", cursor: "pointer",
              transition: "all 0.15s ease", mb: 2,
              "&:hover": { borderColor: T.borderHover },
            }}
          >
            <FileUploadIcon sx={{ fontSize: 30, color: T.primary, mb: 0.5 }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", color: T.text }}>
              {fileName || "Drop CSV here or click to upload"}
            </Typography>
            <Typography sx={{ fontSize: "0.74rem", color: T.textMuted, mt: 0.25 }}>
              Columns: phoneNumber (required), userName, customData
            </Typography>
            <input id="bulk-csv-input" type="file" accept=".csv" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
          </Box>

          {validating && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, color: T.textMuted }}>
              <CircularProgress size={14} /> <Typography sx={{ fontSize: "0.8rem" }}>Validating…</Typography>
            </Box>
          )}

          {validation && !validating && (
            <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                label={`${validation.validCount} valid`}
                size="small"
                sx={{ background: T.successSoft, color: T.success, fontWeight: 600, border: "1px solid #bbf7d0" }}
              />
              {validation.errorCount > 0 && (
                <Chip
                  label={`${validation.errorCount} skipped`}
                  size="small"
                  sx={{ background: T.dangerSoft, color: T.danger, fontWeight: 600, border: "1px solid #fecaca" }}
                />
              )}
            </Box>
          )}

          {/* Caller ID */}
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Caller ID
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
            <InputLabel>Caller ID (from number)</InputLabel>
            <Select value={fromNumberId} label="Caller ID (from number)" onChange={(e) => setFromNumberId(e.target.value)}>
              <MenuItem value="">
                <Typography sx={{ fontSize: "0.85rem", color: T.textMuted, fontWeight: 500 }}>
                  {phoneNumbersLoading ? "Loading…" : "Use default sender"}
                </Typography>
              </MenuItem>
              {phoneNumbers.map((n) => (
                <MenuItem key={n.id} value={n.id}>
                  <Box>
                    <Typography sx={{ fontSize: "0.87rem", fontWeight: 600, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{n.phoneNumber}</Typography>
                    {n.label && <Typography sx={{ fontSize: "0.7rem", color: T.textMuted }}>{n.label}</Typography>}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Voice */}
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Voice
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
            <InputLabel>Select a voice</InputLabel>
            <Select value={voiceCloneId} label="Select a voice" onChange={(e) => setVoiceCloneId(e.target.value)}>
              {clones.length === 0 && (
                <MenuItem disabled value="">
                  <Typography sx={{ fontSize: "0.78rem", color: T.textFaint, fontStyle: "italic" }}>
                    No voices saved — add one in Voice Library
                  </Typography>
                </MenuItem>
              )}
              {clones.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <VoiceAvatar name={c.voiceName} size={26} />
                    <Box>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{c.voiceName}</Typography>
                      {c.description && <Typography sx={{ fontSize: "0.7rem", color: T.textMuted }}>{c.description}</Typography>}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Scenario */}
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Scenario Template
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
            <InputLabel>Load from library (optional)</InputLabel>
            <Select value={scenarioId} label="Load from library (optional)" onChange={(e) => handleScenarioSelect(e.target.value)}>
              <MenuItem value={CUSTOM_SCENARIO_OPTION.id}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: T.textMuted }}>{CUSTOM_SCENARIO_OPTION.name}</Typography>
              </MenuItem>
              {SCENARIOS.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <Box>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{s.name}</Typography>
                    {s.description && (
                      <Typography sx={{ fontSize: "0.7rem", color: T.textMuted, whiteSpace: "normal", lineHeight: 1.35 }}>{s.description}</Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Scenario Prompt
            {scenarioId !== CUSTOM_SCENARIO_OPTION.id && (
              <Typography component="span" sx={{ ml: 1, fontSize: "0.68rem", fontWeight: 500, color: T.textFaint, textTransform: "none", letterSpacing: 0 }}>
                — loaded from template, edit freely
              </Typography>
            )}
          </Typography>
          <TextField
            fullWidth multiline rows={8} size="small"
            value={scenarioPrompt}
            onChange={(e) => setScenarioPrompt(e.target.value)}
            placeholder="Pick a template above, or write your own scenario. This same prompt is used for every recipient in the campaign."
            sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.88rem" } }}
          />

          {/* Rate limit */}
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Pacing
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
            <InputLabel>Rate limit</InputLabel>
            <Select value={rateLimit} label="Rate limit" onChange={(e) => setRateLimit(e.target.value)}>
              <MenuItem value={1}>1 call / minute — stealthiest</MenuItem>
              <MenuItem value={5}>5 calls / minute — recommended</MenuItem>
              <MenuItem value={10}>10 calls / minute — fast</MenuItem>
              <MenuItem value={20}>20 calls / minute — lab only</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained" size="large"
              onClick={handleDispatch}
              disabled={!canDispatch}
              startIcon={dispatching ? <CircularProgress size={18} color="inherit" /> : <GroupsIcon />}
              sx={{
                background: T.primary, "&:hover": { background: T.primaryDark },
                textTransform: "none", fontWeight: 600, borderRadius: "8px",
                px: 3.5, py: 1.25, boxShadow: "none", fontSize: "0.92rem",
              }}
            >
              {dispatching ? "Dispatching…" : validCount > 0 ? `Dispatch ${validCount} Calls` : "Dispatch Campaign"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Right — sidebar */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Card sx={{ border: `1px solid ${T.border}`, boxShadow: "none", borderRadius: "12px", background: T.infoSoft }}>
          <CardContent sx={{ p: "20px !important" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <InsightsIcon sx={{ fontSize: 22, color: T.info, flexShrink: 0, mt: 0.25 }} />
              <Box>
                <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: T.text, mb: 0.5 }}>
                  Results flow into Operations
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: T.textMuted, lineHeight: 1.55 }}>
                  Every dispatched call appears in the <strong>Operations</strong> tab with the same live status, transcript and auto-extracted data as a single call.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ border: `1px solid ${T.border}`, boxShadow: "none", borderRadius: "12px", background: T.warningSoft }}>
          <CardContent sx={{ p: "20px !important" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <BoltIcon sx={{ fontSize: 22, color: T.warning, flexShrink: 0, mt: 0.25 }} />
              <Box>
                <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: T.text, mb: 0.5 }}>
                  Rate-limited dispatch
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: T.textMuted, lineHeight: 1.55 }}>
                  At 5 calls/minute, a 100-recipient campaign completes in about 20 minutes — spacing calls 12 seconds apart to stay under provider limits.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ border: `1px solid ${T.border}`, boxShadow: "none", borderRadius: "12px" }}>
          <CardContent sx={{ p: "20px !important" }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}>
              Campaign Flow
            </Typography>
            <Stack spacing={2}>
              {[
                { n: 1, t: "Upload & Validate", d: "CSV is parsed; invalid numbers are flagged and skipped." },
                { n: 2, t: "Queue & Pace", d: "Valid recipients are queued and dialed at your chosen rate." },
                { n: 3, t: "Live Conversation", d: "Each caller speaks with the AI persona in your selected voice." },
                { n: 4, t: "Data Capture", d: "OTPs, account numbers and IDs are auto-extracted per call." },
                { n: 5, t: "Review in Operations", d: "Filter, open transcripts and export PDFs like any single call." },
              ].map((step) => (
                <Box key={step.n} sx={{ display: "flex", gap: 1.5 }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: T.primarySoft, color: T.primary,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.72rem", fontWeight: 700,
                  }}>
                    {step.n}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: T.text, lineHeight: 1.3 }}>{step.t}</Typography>
                    <Typography sx={{ fontSize: "0.74rem", color: T.textMuted, lineHeight: 1.45, mt: 0.25 }}>{step.d}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
